/**
 * CRM Multi-Compagnia · Lista Compagnie
 * CRUD + drawer dettaglio con tab + reveal credenziali
 */
import { useState } from "react";
import { Building2, Plus, Lock, Edit3, Trash2 } from "lucide-react";
import {
  api, useApi, useDebounced, DataTable, Spinner, ErrorBox, Btn, Drawer,
  RamoChip, CompanyAvatar, Field, TextInput, Select, RevealCredentialsModal,
  StatusBadge, toast, fmtDate
} from "@/components/crm-multi/shared";

const EMPTY = {
  nome: "", slug: "", piva: "", codice_ivass: "", sito_web: "",
  portale_url: "", note: "", attiva: true,
  credenziali_chiare: { username: "", password: "" },
  external_sia_company_id: "",
};

export default function CrmMultiCompagniePage() {
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("");
  const dq = useDebounced(q);
  const list = useApi(() => api.get("/companies", { q: dq || undefined, attiva: stato || undefined, limit: 200 }), [dq, stato]);
  const [drawerId, setDrawerId] = useState(null);
  const [editing, setEditing] = useState(null); // {mode:"new"|"edit", data}
  const [credModal, setCredModal] = useState(null);

  const items = list.data?.items || [];
  const selected = items.find(c => c.id === drawerId);

  const onSave = async (form) => {
    try {
      const body = { ...form };
      // Pulisci credenziali_chiare se non compilato
      if (!body.credenziali_chiare?.username && !body.credenziali_chiare?.password) {
        delete body.credenziali_chiare;
      }
      if (editing.mode === "new") {
        await api.post("/companies", body);
        toast("Compagnia creata", "success");
      } else {
        await api.patch(`/companies/${editing.data.id}`, body);
        toast("Compagnia aggiornata", "success");
      }
      setEditing(null); list.refresh();
    } catch (e) {
      toast(e?.response?.data?.detail?.error_code || e.message, "error");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare definitivamente la compagnia?")) return;
    try { await api.del(`/companies/${id}`); toast("Compagnia eliminata", "success"); list.refresh(); setDrawerId(null); }
    catch (e) { toast(e?.response?.data?.detail?.error_code || e.message, "error"); }
  };

  return (
    <div className="space-y-4" data-testid="cm-compagnie-page">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
            <Building2 className="w-5 h-5" /> Compagnie
          </h2>
          <p className="text-sm text-zinc-500">{list.data?.total ?? 0} compagnie totali</p>
        </div>
        <Btn variant="primary" size="md" onClick={() => setEditing({ mode: "new", data: EMPTY })} data-testid="cm-compagnie-new">
          <Plus className="w-4 h-4" /> Nuova compagnia
        </Btn>
      </header>

      <div className="flex flex-col md:flex-row gap-2">
        <TextInput placeholder="Cerca nome, slug, P.IVA..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="cm-compagnie-search" />
        <Select value={stato} onChange={(e) => setStato(e.target.value)} className="md:w-48" data-testid="cm-compagnie-filter-stato">
          <option value="">Tutti gli stati</option>
          <option value="true">Solo attive</option>
          <option value="false">Solo non attive</option>
        </Select>
      </div>

      <ErrorBox error={list.error} />
      {list.loading ? <Spinner label="Caricamento..." /> : (
        <DataTable
          testid="cm-compagnie-table"
          onRowClick={(r) => setDrawerId(r.id)}
          columns={[
            { key: "nome", label: "Compagnia", render: r => (
              <div className="flex items-center gap-2"><CompanyAvatar nome={r.nome} size="sm" /><div>
                <p className="font-medium text-zinc-900">{r.nome}</p>
                <p className="text-[10px] text-zinc-400 font-mono">{r.slug}</p>
              </div></div>
            )},
            { key: "piva", label: "P.IVA", render: r => <span className="font-mono text-xs">{r.piva || "—"}</span> },
            { key: "codice_ivass", label: "Cod. IVASS", render: r => <span className="font-mono text-xs">{r.codice_ivass || "—"}</span> },
            { key: "attiva", label: "Stato", render: r => <StatusBadge tipo="generic" valore={r.attiva ? "attivo" : "ex"} /> },
            { key: "external_sia_company_id", label: "SIA ID", render: r => <span className="font-mono text-[10px] text-zinc-500">{r.external_sia_company_id || "—"}</span> },
          ]}
          rows={items}
          emptyTitle="Nessuna compagnia ancora"
          emptyHint="Aggiungi la prima compagnia o esegui il seed demo dalla Dashboard."
        />
      )}

      <Drawer
        open={!!selected} onClose={() => setDrawerId(null)}
        title={selected?.nome}
        subtitle={`Slug: ${selected?.slug} · P.IVA: ${selected?.piva || "—"}`}
        actions={selected && (
          <>
            <Btn size="sm" variant="outline" onClick={() => setCredModal(selected)} data-testid="cm-compagnie-creds-btn">
              <Lock className="w-3.5 h-3.5" /> Credenziali
            </Btn>
            <Btn size="sm" variant="outline" onClick={() => setEditing({ mode: "edit", data: selected })}>
              <Edit3 className="w-3.5 h-3.5" /> Modifica
            </Btn>
            <Btn size="sm" variant="danger" onClick={() => onDelete(selected.id)}>
              <Trash2 className="w-3.5 h-3.5" /> Elimina
            </Btn>
          </>
        )}
        testid="cm-compagnie-drawer"
      >
        {selected && <CompagniaDetailTabs company={selected} />}
      </Drawer>

      {editing && <CompagniaEditModal data={editing.data} mode={editing.mode} onSave={onSave} onClose={() => setEditing(null)} />}
      {credModal && <RevealCredentialsModal companyId={credModal.id} companyNome={credModal.nome} onClose={() => setCredModal(null)} />}
    </div>
  );
}

// ----------------------------------------------------------------------------
function CompagniaDetailTabs({ company }) {
  const [tab, setTab] = useState("dati");
  const prodotti = useApi(() => api.get("/products", { company_id: company.id, limit: 100 }), [company.id]);
  const polizze = useApi(() => api.get("/policies", { company_id: company.id, limit: 50 }), [company.id]);

  return (
    <div>
      <nav className="flex gap-1 border-b border-zinc-200 mb-4">
        {[
          ["dati", "Dati generali"],
          ["prodotti", `Prodotti (${prodotti.data?.total ?? "..."})`],
          ["polizze", `Polizze (${polizze.data?.total ?? "..."})`],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === id ? "border-emerald-600 text-emerald-700" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
            {label}
          </button>
        ))}
      </nav>

      {tab === "dati" && (
        <dl className="grid grid-cols-2 gap-y-3 text-sm">
          <KV k="Nome" v={company.nome} />
          <KV k="Slug" v={<code className="text-xs">{company.slug}</code>} />
          <KV k="P.IVA" v={company.piva} />
          <KV k="Codice IVASS" v={company.codice_ivass} />
          <KV k="Sito web" v={company.sito_web && <a href={company.sito_web} target="_blank" rel="noreferrer" className="text-emerald-700 underline">{company.sito_web}</a>} />
          <KV k="Portale agenti" v={company.portale_url && <a href={company.portale_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline">{company.portale_url}</a>} />
          <KV k="Stato" v={<StatusBadge tipo="generic" valore={company.attiva ? "attivo" : "ex"} />} />
          <KV k="External SIA ID" v={<code className="text-xs">{company.external_sia_company_id || "—"}</code>} />
          <KV k="Creata" v={fmtDate(company.created_at)} />
          <KV k="Aggiornata" v={fmtDate(company.updated_at)} />
          <div className="col-span-2"><KV k="Note" v={company.note || "—"} /></div>
          <div className="col-span-2">
            <span className="text-xs text-zinc-500">Credenziali</span>
            <p className="text-sm text-amber-700">🔒 Cifrate Fernet — usa pulsante "Credenziali" in alto</p>
          </div>
        </dl>
      )}
      {tab === "prodotti" && (
        <div className="space-y-2">
          {prodotti.loading ? <Spinner /> : (prodotti.data?.items || []).length === 0 ? (
            <p className="text-sm text-zinc-500">Nessun prodotto collegato.</p>
          ) : prodotti.data.items.map(p => (
            <div key={p.id} className="p-3 border border-zinc-200 rounded">
              <p className="font-medium text-sm">{p.nome}</p>
              <p className="text-xs text-zinc-500">{p.codice_prodotto} · {p.target_clientela}</p>
              <div className="flex gap-1 mt-1 flex-wrap">{p.rami_supportati.map(r => <RamoChip key={r} ramo={r} />)}</div>
            </div>
          ))}
        </div>
      )}
      {tab === "polizze" && (
        <div className="space-y-2">
          {polizze.loading ? <Spinner /> : (polizze.data?.items || []).length === 0 ? (
            <p className="text-sm text-zinc-500">Nessuna polizza per questa compagnia.</p>
          ) : polizze.data.items.map(p => (
            <div key={p.id} className="p-2.5 border border-zinc-200 rounded flex items-center justify-between text-sm">
              <div>
                <span className="font-mono text-xs">{p.numero_polizza}</span>
                <RamoChip ramo={p.ramo} /> 
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-zinc-500">scad. {fmtDate(p.data_scadenza)}</span>
                <StatusBadge tipo="polizza" valore={p.stato} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const KV = ({ k, v }) => (
  <div>
    <dt className="text-[10px] uppercase font-medium text-zinc-500 tracking-wider">{k}</dt>
    <dd className="text-sm text-zinc-900 mt-0.5 break-words">{v || "—"}</dd>
  </div>
);

// ----------------------------------------------------------------------------
function CompagniaEditModal({ data, mode, onSave, onClose }) {
  const [form, setForm] = useState({ ...data, credenziali_chiare: data.credenziali_chiare || { username: "", password: "" } });
  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cm-compagnia-edit">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-zinc-100 flex justify-between items-center">
          <h3 className="text-sm font-semibold">{mode === "new" ? "Nuova compagnia" : `Modifica · ${data.nome}`}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 text-xl leading-none">×</button>
        </header>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="p-5 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Nome" required><TextInput required value={form.nome} onChange={(e) => u("nome", e.target.value)} data-testid="cm-compagnia-form-nome" /></Field>
            <Field label="Slug (URL-safe)" required hint="Lowercase, no spazi, es. generali-italia"><TextInput required value={form.slug} onChange={(e) => u("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))} data-testid="cm-compagnia-form-slug" /></Field>
            <Field label="Partita IVA"><TextInput value={form.piva || ""} onChange={(e) => u("piva", e.target.value)} /></Field>
            <Field label="Codice IVASS"><TextInput value={form.codice_ivass || ""} onChange={(e) => u("codice_ivass", e.target.value)} /></Field>
            <Field label="Sito web"><TextInput type="url" value={form.sito_web || ""} onChange={(e) => u("sito_web", e.target.value)} /></Field>
            <Field label="Portale agenti URL"><TextInput type="url" value={form.portale_url || ""} onChange={(e) => u("portale_url", e.target.value)} /></Field>
            <Field label="External SIA ID" hint="ID da SIA Omnia 8"><TextInput value={form.external_sia_company_id || ""} onChange={(e) => u("external_sia_company_id", e.target.value)} /></Field>
            <Field label="Stato">
              <Select value={String(form.attiva)} onChange={(e) => u("attiva", e.target.value === "true")}>
                <option value="true">Attiva</option><option value="false">Non attiva</option>
              </Select>
            </Field>
          </div>
          <Field label="Note"><textarea className="w-full px-3 py-1.5 text-sm border border-zinc-300 rounded" rows={2} value={form.note || ""} onChange={(e) => u("note", e.target.value)} /></Field>

          <div className="border-t pt-3 mt-3">
            <p className="text-xs font-medium text-zinc-700 mb-2">🔒 Credenziali portale agenti (cifrate Fernet)</p>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Username"><TextInput value={form.credenziali_chiare?.username || ""} onChange={(e) => u("credenziali_chiare", { ...form.credenziali_chiare, username: e.target.value })} /></Field>
              <Field label="Password"><TextInput type="password" value={form.credenziali_chiare?.password || ""} onChange={(e) => u("credenziali_chiare", { ...form.credenziali_chiare, password: e.target.value })} /></Field>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t">
            <Btn type="button" variant="outline" size="sm" onClick={onClose}>Annulla</Btn>
            <Btn type="submit" variant="primary" size="sm" data-testid="cm-compagnia-form-save">Salva</Btn>
          </div>
        </form>
      </div>
    </div>
  );
}
